import { Icon, IconButton, MenuItem } from "@material-ui/core";
import React, { Fragment, useRef } from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
} from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { t } from "js/i18n";
import {
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowInputDialog,
    ShowWarningDialog,
} from "js/react/components/Dialogs/BaseDialog";
import { DeleteWorkspaceResourceFolderDialog } from "js/react/components/Dialogs/DeleteWorkspaceResourceFolderDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import { FlexSpacer } from "js/react/components/Gap";
import {
    FolderContainer,
    LibraryFolder,
    selectedClass,
    SideBarLabel,
    StyledAddIcon,
    StyledFoldersSection,
} from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";

const logger = getLogger();

const RestrictedMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

function isWorkspaceFolderNameTaken(
    name: string,
    folders: IWorkspaceResourceFolder[],
    resourceTypes: WorkspaceResourceType[],
    { parentFolderId, excludeFolderId }: { parentFolderId?: string | null; excludeFolderId?: string }
) {
    return folders
        .filter(f => {
            if (!resourceTypes.includes(f.resourcesType)) {
                return false;
            }
            if (excludeFolderId && f.id === excludeFolderId) {
                return false;
            }
            if (parentFolderId) {
                return f.parentFolderId === parentFolderId;
            }
            return !f.parentFolderId;
        })
        .some(f => f.name === name);
}

const CREATE_FOLDER_CONFIG: Partial<Record<WorkspaceResourceType, { inputId: string; logTag: string }>> = {
    [WorkspaceResourceType.SLIDE]: {
        inputId: "create_slide_folder_input",
        logTag: "WorkspaceResourceSideBarFolders",
    },
    [WorkspaceResourceType.SLIDE_TEMPLATE]: {
        inputId: "create_slide_template_folder_input",
        logTag: "WorkspaceResourceSideBarFolders",
    },
    [WorkspaceResourceType.ASSET]: {
        inputId: "create_asset_folder_input",
        logTag: "WorkspaceResourceSideBarFolders",
    },
};

type WorkspaceResourceSideBarFolderProps = {
    folder: IWorkspaceResourceFolder;
    selectedFolderId?: string | null;
    onSelectFolder?: (folderId: string | null) => void;
    readOnly?: boolean;
    resourceTypes: WorkspaceResourceType[];
    resourceFolders: IWorkspaceResourceFolder[];
    draggedItems?: unknown[];
    onFolderDrop?: (folder: IWorkspaceResourceFolder) => void | Promise<void>;
    onCreateFolder?: (parentFolder?: IWorkspaceResourceFolder) => void | Promise<void>;
};

function WorkspaceResourceSideBarFolder({
    folder,
    selectedFolderId = null,
    onSelectFolder,
    readOnly = false,
    resourceTypes,
    resourceFolders,
    draggedItems,
    onFolderDrop,
    onCreateFolder,
}: WorkspaceResourceSideBarFolderProps) {
    const isSubFolder = Boolean(folder.parentFolderId);
    const ignoreNextFolderMouseUpRef = useRef<boolean>(false);

    const canRenameFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToRenameFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.RESOURCE_FOLDERS,
        PermissionActionType.CREATE
    );
    const canDeleteFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToDeleteFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.RESOURCE_FOLDERS,
        PermissionActionType.CREATE
    );

    const handleCanUpgradeAction = (cta: string) => () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.PRO, analytics: { cta } });
        }
    };

    const isFolderNameTaken = (name: string) => isWorkspaceFolderNameTaken(name, resourceFolders, resourceTypes, {
        parentFolderId: folder.parentFolderId,
        excludeFolderId: folder.id,
    });

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t(isSubFolder ? "Rename Subfolder" : "Rename Folder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isFolderNameTaken(name)) {
                throw new Error(t(isSubFolder ? "Please enter a unique subfolder name." : "Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t(isSubFolder ? "Renaming subfolder..." : "Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[WorkspaceResourceSideBarFolders] handleRenameFolder failed");

            ShowErrorDialog({
                title: t(isSubFolder ? "Unable to rename subfolder" : "Unable to rename folder"),
                message: (err as Error).message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        const deleted = await ShowDialogAsync(DeleteWorkspaceResourceFolderDialog, {
            folder,
            resourceFolders,
            resourceTypes,
        });

        if (!deleted || folder.id !== selectedFolderId) {
            return;
        }

        onSelectFolder?.(folder.parentFolderId ?? null);
    };

    const handleMouseUp = async (event: React.MouseEvent | React.TouchEvent) => {
        if (ignoreNextFolderMouseUpRef.current) {
            return;
        }

        if ((draggedItems?.length ?? 0) > 0) {
            event.stopPropagation();
            event.preventDefault();
            await onFolderDrop?.(folder);
            return;
        }

        onSelectFolder?.(folder.id);
    };

    return (
        <LibraryFolder
            $nested={isSubFolder}
            data-attr={folder.name}
            className={selectedClass(selectedFolderId === folder.id)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <Icon className="material-symbols-outlined">{isSubFolder ? "subdirectory_arrow_right" : "folder"}</Icon>
            <div className="folder-name">{folder.name}</div>
            <FlexSpacer />
            {!readOnly && !app.isConstrained &&
                <IconMenu onShowMenu={() => {
                    ignoreNextFolderMouseUpRef.current = true;
                }}
                          handleCloseMenu={() => {
                              ignoreNextFolderMouseUpRef.current = false;
                          }}
                >
                    {canRenameFolders &&
                        <MenuItem onClick={handleRenameFolder}>
                            <Icon>label</Icon>
                            {t(isSubFolder ? "Rename Subfolder" : "Rename Folder")}
                        </MenuItem>}
                    {canUpgradeToRenameFolders && (
                        <RestrictedMenuItem divider onClick={handleCanUpgradeAction("rename_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t(isSubFolder ? "Rename Subfolder" : "Rename Folder")}
                        </RestrictedMenuItem>
                    )}

                    {!isSubFolder && canRenameFolders && (
                        <MenuItem onClick={() => onCreateFolder?.(folder)}>
                            <Icon>folder_open</Icon>
                            {t("Create Subfolder")}
                        </MenuItem>)}

                    <hr />
                    {canDeleteFolders &&
                        <MenuItem onClick={handleDeleteFolder}>
                            <Icon>delete</Icon>
                            {t(isSubFolder ? "Delete Subfolder" : "Delete Folder")}
                        </MenuItem>}

                    {canUpgradeToDeleteFolders &&
                        <RestrictedMenuItem onClick={handleCanUpgradeAction("delete_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t(isSubFolder ? "Delete Subfolder" : "Delete Folder")}
                        </RestrictedMenuItem>}
                </IconMenu>
            }
        </LibraryFolder>
    );
}

export type WorkspaceResourceSideBarFoldersSectionProps = {
    folders: IWorkspaceResourceFolder[];
    selectedFolderId?: string | null;
    onSelectFolder?: (folderId: string | null) => void;
    readOnly?: boolean;
    resourceTypes: WorkspaceResourceType[];
    folderType?: WorkspaceResourceFolderType;
    createResourcesType?: WorkspaceResourceType;
    createFolderButtonId?: string;
    shareCreatedFoldersWithDefaultGroups?: boolean;
    draggedItems?: unknown[];
    onFolderDrop?: (folder: IWorkspaceResourceFolder) => void | Promise<void>;
};

export function WorkspaceResourceSideBarFoldersSection({
    folders,
    selectedFolderId = null,
    onSelectFolder,
    readOnly = false,
    resourceTypes,
    folderType = WorkspaceResourceFolderType.SHARED,
    createResourcesType,
    createFolderButtonId,
    shareCreatedFoldersWithDefaultGroups = true,
    draggedItems,
    onFolderDrop,
}: WorkspaceResourceSideBarFoldersSectionProps) {
    const resourceFolders = WorkspaceController.resourceFolders;
    const canCreateFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const resourcesTypeForCreate = createResourcesType ?? resourceTypes[0];
    const createConfig = resourcesTypeForCreate ? CREATE_FOLDER_CONFIG[resourcesTypeForCreate] : undefined;

    const handleCreateFolder = async (parentFolder?: IWorkspaceResourceFolder) => {
        const isSubFolder = Boolean(parentFolder);

        if (!canCreateFolders) {
            if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE)) {
                ShowDialog(ChangePlanDialog, {
                    featuredPlan: WorkspacePlanTier.TEAM,
                });
            } else {
                ShowWarningDialog({
                    title: t("Oops!"),
                    message: t("You do not have permission to create folders!"),
                });
            }
            return;
        }

        const folderName = await new Promise<string | undefined>(resolve => ShowDialog(InputConfirmationDialog, {
            title: t(isSubFolder ? "Create Subfolder" : "Create Folder"),
            input: {
                type: "text",
                placeholder: t("Type folder name..."),
                id: createConfig?.inputId,
                inputProps: {
                    maxLength: 50,
                },
            },
            acceptCallback: (input: string) => resolve(input.trim()),
        }));

        if (!folderName) {
            return;
        }

        if (isWorkspaceFolderNameTaken(folderName, resourceFolders, resourceTypes, { parentFolderId: parentFolder?.id ?? null })) {
            ShowWarningDialog({
                title: t(isSubFolder ? "Unable to create subfolder" : "Unable to create folder"),
                message: t(isSubFolder ? "Please enter a unique subfolder name." : "Please enter a unique folder name."),
            });
            return;
        }

        const resourcesType = parentFolder?.resourcesType ?? resourcesTypeForCreate;
        const folderTypeToCreate = parentFolder?.folderType ?? folderType;

        if (!resourcesType) {
            return;
        }

        try {
            const folder = await withProgressDialog({
                title: t(isSubFolder ? "Creating subfolder..." : "Creating folder..."),
                action: () => {
                    const createRequest = {
                        name: folderName,
                        resourcesType,
                        folderType: folderTypeToCreate,
                        ...(parentFolder ? { parentFolderId: parentFolder.id } : {}),
                    };

                    if (!parentFolder && shareCreatedFoldersWithDefaultGroups) {
                        return WorkspaceController.createWorkspaceResourceFolderAndShareWithDefaultGroups(createRequest);
                    }

                    return WorkspaceController.createWorkspaceResourceFolder(createRequest);
                }
            });

            onSelectFolder?.(folder.id);
        } catch (err) {
            logger.error(err, `[${createConfig?.logTag}] handleCreateFolder failed`);

            ShowErrorDialog({
                title: t(isSubFolder ? "Unable to create subfolder" : "Unable to create folder"),
                message: (err as Error).message,
            });
        }
    };

    return (
        <StyledFoldersSection>
            <SideBarLabel>
                <span>{t("Folders")}</span>
                {!readOnly && !app.isConstrained && canCreateFolders &&
                    <IconButton size="small" id={createFolderButtonId} onClick={() => handleCreateFolder()}>
                        <StyledAddIcon>add</StyledAddIcon>
                    </IconButton>
                }
            </SideBarLabel>
            <FolderContainer>
                {folders.map(parentFolder => {
                    const foldersForType = resourceFolders.filter(f => f.resourcesType === parentFolder.resourcesType);
                    const subFolders = foldersForType
                        .filter(f => f.parentFolderId === parentFolder.id)
                        .sort((a, b) => a.name.localeCompare(b.name));
                    const folderRowProps = {
                        selectedFolderId,
                        onSelectFolder,
                        readOnly,
                        resourceTypes,
                        resourceFolders: foldersForType,
                        draggedItems,
                        onFolderDrop,
                        onCreateFolder: handleCreateFolder,
                    };

                    return (
                        <Fragment key={parentFolder.id}>
                            <WorkspaceResourceSideBarFolder folder={parentFolder} {...folderRowProps} />
                            {subFolders.map(subFolder => (
                                <WorkspaceResourceSideBarFolder
                                    key={subFolder.id}
                                    folder={subFolder}
                                    {...folderRowProps}
                                />
                            ))}
                        </Fragment>
                    );
                })}
            </FolderContainer>
        </StyledFoldersSection>
    );
}
